import { apiClient, getAccessToken, refreshAccessToken } from './client';

/**
 * Near-real-time session streaming (tenant_admin viewer side).
 *
 * The capture-app ships NDJSON chunks to S3 while recording. The portal
 * discovers in-progress sessions (`listLiveSessions`) and then opens an
 * SSE stream (`streamLive`) that the server pushes new chunks down the
 * instant they land. `getLiveChunks` is a polling fallback.
 */

export interface LiveSessionSummary {
  session_id: string;
  participant_label: string | null;
  /** The participant's user id — maps a Live Recording overlay participant to
   *  their in-progress session for the per-person drill-down. */
  participant_user_id: string | null;
  procedure_name: string | null;
  started_at: string | null;
  /** Edge cameras that stopped sending video during this session (agent
   *  0.3.27+). Optional: the participant's own live list doesn't carry it. */
  frozen_cameras?: FrozenCamera[];
  /** ROLES-AND-GO: recording, but waiting for Go (a two-stage start). */
  standby?: boolean;
  /** Go was already pressed here; the phone starts within a heartbeat. */
  go_requested?: boolean;
}

export interface FrozenCamera {
  camera_id: string;
  name: string | null;
  /** e.g. "no video since 0:42 into the recording — check its USB cable". */
  detail: string | null;
  since: string | null;
}

export interface LiveChunks {
  /** Concatenated NDJSON of chunks newer than the request cursor. */
  ndjson: string;
  /** New cursor — pass as `since` on the next poll. */
  cursor: number;
  /** The device marked the session ended. */
  ended: boolean;
  /** Still streaming (meta present, not ended, recent). */
  live: boolean;
}

/** In-progress sessions a tenant_admin can watch live. */
export async function listLiveSessions(
  tenantId: string,
): Promise<LiveSessionSummary[]> {
  const { data } = await apiClient.get<LiveSessionSummary[]>(
    `/api/v1/tenants/${tenantId}/live`,
  );
  return data;
}

/** In-progress sessions recording for the logged-in participant (their
 *  own only — server gates on the live meta's participant_user_id). */
export async function listMyLiveSessions(): Promise<LiveSessionSummary[]> {
  const { data } = await apiClient.get<LiveSessionSummary[]>(
    `/api/v1/sessions/me/live`,
  );
  return data;
}

/** Poll for chunks newer than `since` (0 to start from the beginning). */
export async function getLiveChunks(
  tenantId: string,
  sessionId: string,
  since: number,
): Promise<LiveChunks> {
  const { data } = await apiClient.get<LiveChunks>(
    `/api/v1/tenants/${tenantId}/live/${sessionId}/chunks`,
    { params: { since } },
  );
  return data;
}

/** Poll fallback for the participant's own live session. */
export async function getMyLiveChunks(
  sessionId: string,
  since: number,
): Promise<LiveChunks> {
  const { data } = await apiClient.get<LiveChunks>(
    `/api/v1/sessions/me/live/${sessionId}/chunks`,
    { params: { since } },
  );
  return data;
}

export interface LiveStreamHandlers {
  /** The SSE transport is open (server sent its initial `ready` frame),
   *  before any chunk — lets the viewer distinguish "connecting" from
   *  "connected, waiting for the device's first sample". */
  onOpen?: () => void;
  /** A new batch of NDJSON arrived; `cursor` is the new high-water seq. */
  onChunk: (ndjson: string, cursor: number) => void;
  /** The session ended (device sent /end) or went stale (device gone). */
  onEnded: () => void;
}

/** One parsed SSE frame. */
function parseSseFrame(frame: string): { event: string; data: string } {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue; // keep-alive comment
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  return { event, data: dataLines.join('\n') };
}

/**
 * Open a Server-Sent Events stream of live chunks and push each one to
 * `handlers.onChunk` as it arrives. Returns a stop function — call it
 * (e.g. on dialog close) to abort the connection.
 *
 * Uses fetch + ReadableStream rather than the native EventSource so we
 * can (a) carry the access token, (b) reconnect from the last cursor
 * (avoiding a re-backfill of everything), and (c) silently refresh an
 * expired token mid-stream. Auto-reconnects on transport errors until
 * the session ends or the caller stops it.
 */
export function streamLive(
  tenantId: string,
  sessionId: string,
  startSince: number,
  handlers: LiveStreamHandlers,
): () => void {
  return streamFrom(
    `/api/v1/tenants/${tenantId}/live/${sessionId}/stream`,
    startSince,
    handlers,
  );
}

/**
 * Participant variant of `streamLive` — streams the participant's OWN
 * live session (server proves ownership from the live meta). Same
 * mechanics; only the URL scope differs.
 */
export function streamMyLive(
  sessionId: string,
  startSince: number,
  handlers: LiveStreamHandlers,
): () => void {
  return streamFrom(
    `/api/v1/sessions/me/live/${sessionId}/stream`,
    startSince,
    handlers,
  );
}

/**
 * Subscribe to the tenant's live-DISCOVERY SSE: the server pushes a `changed`
 * event whenever a session starts or stops, so the caller re-fetches
 * `listLiveSessions` immediately instead of waiting out its poll (the meta was
 * previously only found by an S3 LIST that lagged ~15 s). Carries no data.
 * Returns a stop function; auto-reconnects with token refresh, like streamLive.
 */
export function streamLiveDiscovery(
  tenantId: string,
  onChanged: () => void,
): () => void {
  let stopped = false;
  let ctrl: AbortController | null = null;
  const base = apiClient.defaults.baseURL ?? '';
  const urlFor = (token: string | null): string =>
    `${base}/api/v1/tenants/${tenantId}/live/stream?token=${encodeURIComponent(
      token ?? '',
    )}`;

  async function connectOnce(): Promise<void> {
    ctrl = new AbortController();
    const opts = {
      signal: ctrl.signal,
      credentials: 'include' as const,
      headers: { Accept: 'text/event-stream' },
    };
    let res = await fetch(urlFor(getAccessToken()), opts);
    if (res.status === 401) {
      const fresh = await refreshAccessToken();
      if (stopped) return;
      res = await fetch(urlFor(fresh), opts);
    }
    if (!res.ok || !res.body) return;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return;
      buf += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buf.indexOf('\n\n')) >= 0) {
        const frame = buf.slice(0, nl);
        buf = buf.slice(nl + 2);
        if (parseSseFrame(frame).event === 'changed') onChanged();
      }
    }
  }

  (async () => {
    while (!stopped) {
      try {
        await connectOnce();
      } catch {
        if (stopped) return; // abort() during read throws — expected
      }
      if (stopped) return;
      await new Promise((r) => setTimeout(r, 1500));
    }
  })();

  return () => {
    stopped = true;
    ctrl?.abort();
  };
}

/** Shared SSE driver for the tenant + participant live streams. `path`
 *  is the endpoint's path (the `since` + `token` query params are
 *  appended here). */
function streamFrom(
  path: string,
  startSince: number,
  handlers: LiveStreamHandlers,
): () => void {
  let stopped = false;
  let cursor = startSince;
  let ctrl: AbortController | null = null;
  const base = apiClient.defaults.baseURL ?? '';

  const urlFor = (token: string | null): string =>
    `${base}${path}?since=${cursor}&token=${encodeURIComponent(token ?? '')}`;

  // Returns 'ended' if the session finished, 'retry' to reconnect.
  async function connectOnce(): Promise<'ended' | 'retry'> {
    ctrl = new AbortController();
    let res = await fetch(urlFor(getAccessToken()), {
      signal: ctrl.signal,
      credentials: 'include',
      headers: { Accept: 'text/event-stream' },
    });
    if (res.status === 401) {
      const fresh = await refreshAccessToken();
      if (stopped) return 'ended';
      res = await fetch(urlFor(fresh), {
        signal: ctrl.signal,
        credentials: 'include',
        headers: { Accept: 'text/event-stream' },
      });
    }
    if (!res.ok || !res.body) return 'retry';

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return 'retry'; // server closed without `ended` → reconnect
      buf += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buf.indexOf('\n\n')) >= 0) {
        const frame = buf.slice(0, nl);
        buf = buf.slice(nl + 2);
        const { event, data } = parseSseFrame(frame);
        if (event === 'ready') {
          handlers.onOpen?.();
        } else if (event === 'chunk' && data) {
          try {
            const msg = JSON.parse(data) as { ndjson: string; cursor: number };
            cursor = msg.cursor;
            handlers.onChunk(msg.ndjson, msg.cursor);
          } catch {
            /* malformed frame — skip */
          }
        } else if (event === 'ended') {
          return 'ended';
        }
      }
    }
  }

  (async () => {
    while (!stopped) {
      try {
        if ((await connectOnce()) === 'ended') {
          if (!stopped) handlers.onEnded();
          return;
        }
      } catch {
        if (stopped) return; // abort() during read throws — expected
      }
      if (stopped) return;
      // Brief backoff before reconnecting from the last cursor.
      await new Promise((r) => setTimeout(r, 1500));
    }
  })();

  return () => {
    stopped = true;
    ctrl?.abort();
  };
}

/** Go, from the Live page, for a recording in standby (ROLES-AND-GO). The
 *  phone picks it up from its next heartbeat and starts the procedure. */
export async function requestLiveGo(tenantId: string, sessionId: string): Promise<void> {
  await apiClient.post(
    `/api/v1/tenants/${tenantId}/live/${encodeURIComponent(sessionId)}/go`,
  );
}
